import { CSSProperties, ReactElement, ReactNode, UIEventHandler, useRef, useState } from 'react';
import type { ButtonProps, DropdownProps } from 'antd';
import { Dropdown, Menu } from 'antd';
import { TbSearch, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownOptionContent, {
    DropdownOptionAvatar,
    DropdownOptionAvatarProps,
} from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, {
    DropdownPanelMenu,
    FLOATING_DROPDOWN_OVERLAY_STYLE,
} from '@/shared/components/dropdowns/DropdownPanel';
import EmptySection from '@/shared/components/layouts/views/EmptySection';

import DropdownButton from '.';

type DropdownButtonValue = boolean | number | string;

export interface DropdownButtonOption<T extends DropdownButtonValue> {
    avatar?: DropdownOptionAvatarProps;
    description?: ReactNode;
    disabled?: boolean;
    dividerBefore?: boolean;
    icon?: ReactNode;
    label: ReactNode;
    metadata?: ReactNode[];
    searchText?: string;
    value: T;
}

export interface DropdownButtonSelectProps<T extends DropdownButtonValue> extends Omit<
    ButtonProps,
    'children' | 'onChange' | 'onClick' | 'value'
> {
    allowClear?: boolean;
    children?: ReactElement;
    clearLabel?: ReactNode;
    emptyDescription?: ReactNode;
    emptyIcon?: ReactNode;
    emptyLabel?: ReactNode;
    footer?: ReactNode;
    header?: ReactNode;
    matchTriggerWidth?: boolean;
    maxHeight?: CSSProperties['maxHeight'];
    menuMinWidth?: CSSProperties['minWidth'];
    menuWidth?: CSSProperties['width'];
    noResultsDescription?: ReactNode;
    noResultsLabel?: ReactNode;
    onChange?: (value: T | undefined) => void;
    onOpenChange?: DropdownProps['onOpenChange'];
    optionPageSize?: number;
    options?: Array<DropdownButtonOption<T>>;
    placeholder?: ReactNode;
    placement?: DropdownProps['placement'];
    renderOption?: (option: DropdownButtonOption<T>, selected: boolean) => ReactNode;
    renderValue?: (option: DropdownButtonOption<T> | undefined) => ReactNode;
    searchable?: boolean;
    searchPlaceholder?: string;
    showTriggerAvatar?: boolean;
    status?: 'error' | 'warning';
    value?: T;
}

const optionKey = (value: DropdownButtonValue) => `${typeof value}:${String(value)}`;

const optionSearchText = <T extends DropdownButtonValue>(option: DropdownButtonOption<T>) => {
    if (option.searchText) {
        return option.searchText;
    }

    return typeof option.label === 'string' || typeof option.label === 'number' ? String(option.label) : '';
};

const DropdownButtonSelect = <T extends DropdownButtonValue>({
    allowClear,
    children,
    clearLabel = 'Clear selection',
    disabled,
    emptyDescription = 'There are no options available to select.',
    emptyIcon = <TbSearch />,
    emptyLabel = 'No Options Available',
    footer,
    header,
    loading,
    matchTriggerWidth = true,
    maxHeight = 'min(24rem, 70vh)',
    menuMinWidth = '12rem',
    menuWidth,
    noResultsDescription = 'Try another search.',
    noResultsLabel = 'No Matching Options',
    onChange = () => undefined,
    onOpenChange,
    optionPageSize = 40,
    options = [],
    placeholder = 'Select An Option...',
    placement = 'bottomLeft',
    renderOption,
    renderValue,
    searchable,
    searchPlaceholder = 'Search Options...',
    showTriggerAvatar = true,
    status,
    style,
    value,
    ...buttonProps
}: DropdownButtonSelectProps<T>) => {
    const { token } = useAntDesignTheme();
    const triggerRef = useRef<HTMLSpanElement>(null);
    const [search, setSearch] = useState('');
    const [triggerWidth, setTriggerWidth] = useState<number>();
    const [visibleOptionState, setVisibleOptionState] = useState({ context: '', count: optionPageSize });
    const selectedOption = options.find((option) => option.value === value);
    const normalizedSearch = search.trim().toLowerCase();
    const filteredOptions =
        searchable && normalizedSearch
            ? options.filter((option) => optionSearchText(option).toLowerCase().includes(normalizedSearch))
            : options;
    const visibleOptionContext = `${normalizedSearch}\u0000${optionPageSize}\u0000${options.length}`;
    const visibleOptionCount =
        visibleOptionState.context === visibleOptionContext ? visibleOptionState.count : optionPageSize;
    const resolvedMenuWidth = menuWidth ?? (matchTriggerWidth ? triggerWidth : undefined);
    const statusColor = status === 'error' ? token.colorError : status === 'warning' ? token.colorWarning : undefined;
    const visibleOptions = filteredOptions.slice(0, visibleOptionCount);
    const selectedAvatar = selectedOption?.avatar;
    const selectedAvatarIcon = selectedAvatar?.icon ?? selectedOption?.icon;
    const showSelectedAvatar = Boolean(
        selectedAvatar?.resource || selectedAvatarIcon || selectedAvatar?.src || selectedAvatar?.website,
    );

    const handleOpenChange: NonNullable<DropdownProps['onOpenChange']> = (open, info) => {
        if (open && matchTriggerWidth) {
            setTriggerWidth(triggerRef.current?.getBoundingClientRect().width);
        } else if (!open) {
            setSearch('');
            setVisibleOptionState({ context: '', count: optionPageSize });
        }

        onOpenChange?.(open, info);
    };

    const handleMenuScroll: UIEventHandler<HTMLDivElement> = (event) => {
        const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;

        if (scrollHeight - scrollTop - clientHeight <= 48 && visibleOptionCount < filteredOptions.length) {
            setVisibleOptionState({
                context: visibleOptionContext,
                count: Math.min(visibleOptionCount + optionPageSize, filteredOptions.length),
            });
        }
    };

    return (
        <Dropdown
            disabled={disabled || Boolean(loading)}
            placement={placement}
            overlayStyle={FLOATING_DROPDOWN_OVERLAY_STYLE}
            trigger={['click']}
            onOpenChange={handleOpenChange}
            popupRender={() => (
                <DropdownPanel
                    contentProps={{ onScroll: handleMenuScroll }}
                    footer={footer}
                    header={header}
                    maxHeight={maxHeight}
                    minWidth={menuMinWidth}
                    width={resolvedMenuWidth}
                    search={
                        searchable
                            ? {
                                  placeholder: searchPlaceholder,
                                  setValue: setSearch,
                                  value: search,
                              }
                            : undefined
                    }
                    style={placement.startsWith('top') ? { marginBottom: '0.375rem' } : { marginTop: '0.375rem' }}
                >
                    <DropdownPanelMenu
                        selectable={false}
                        style={{ maxHeight: 'none', overflow: 'visible' }}
                        onClick={({ key }) => {
                            if (key === '__clear__') {
                                onChange(undefined);
                                return;
                            }

                            const nextOption = options.find((option) => optionKey(option.value) === key);

                            if (nextOption && !nextOption.disabled) {
                                onChange(nextOption.value);
                            }
                        }}
                    >
                        {visibleOptions.flatMap((option, index) => {
                            const selected = option.value === value;
                            const key = optionKey(option.value);

                            return [
                                option.dividerBefore && index > 0 ? <Menu.Divider key={`divider:${key}`} /> : null,
                                <Menu.Item disabled={option.disabled} key={key}>
                                    {renderOption ? (
                                        renderOption(option, selected)
                                    ) : (
                                        <DropdownOptionContent
                                            avatar={option.avatar}
                                            description={option.description}
                                            icon={option.icon}
                                            metadata={option.metadata}
                                            selected={selected}
                                            title={option.label}
                                        />
                                    )}
                                </Menu.Item>,
                            ];
                        })}
                        {filteredOptions.length === 0 ? (
                            <EmptySection
                                description={normalizedSearch ? noResultsDescription : emptyDescription}
                                height='10rem'
                                icon={emptyIcon}
                                label={normalizedSearch ? noResultsLabel : emptyLabel}
                            />
                        ) : null}
                        {allowClear && value !== undefined && visibleOptions.length >= filteredOptions.length ? (
                            <>
                                <Menu.Divider />
                                <Menu.Item key='__clear__' icon={<TbX />}>
                                    {clearLabel}
                                </Menu.Item>
                            </>
                        ) : null}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <span
                ref={triggerRef}
                style={{ display: children ? 'inline-flex' : 'block', width: children ? undefined : '100%' }}
            >
                {children ?? (
                    <DropdownButton
                        {...buttonProps}
                        block
                        disabled={disabled}
                        loading={loading}
                        style={{ borderColor: statusColor, ...style }}
                    >
                        {showTriggerAvatar && showSelectedAvatar ? (
                            <span
                                data-dropdown-trigger-avatar='true'
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    flex: 'none',
                                    justifyContent: 'center',
                                    lineHeight: 0,
                                }}
                            >
                                <DropdownOptionAvatar {...selectedAvatar} icon={selectedAvatarIcon} />
                            </span>
                        ) : null}
                        <span
                            style={{
                                color: selectedOption ? undefined : token.colorTextPlaceholder,
                                minWidth: 0,
                                overflow: 'hidden',
                                textAlign: 'left',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                            }}
                        >
                            {renderValue ? renderValue(selectedOption) : (selectedOption?.label ?? placeholder)}
                        </span>
                    </DropdownButton>
                )}
            </span>
        </Dropdown>
    );
};

export type { DropdownButtonValue };
export default DropdownButtonSelect;
